import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AUTH_PERMISSIONS,
  PermissionService,
} from '../report-system/core/services/permission.service';
import { UserManagementService } from '../user-management/user-management.service';
import { environment } from '../../environments/environment';
import { UserRoleService } from 'src/app/shared/user-role.service';

type NavbarVariant =
  | 'legacy-header'    // app-header (config_role_id 1)
  | 'shared-user'      // app-shared-user-nav (config_role_id 2, 7, 8, 11, 13, 14, 15)
  | 'corporate-admin'  // app-corporate-admin-nav (config_role_id 6)
  | 'operator'         // app-operator-nav (config_role_id 3, 9)
  | 'executive'        // app-executive-nav (config_role_id 12)
  | 'user-management'  // user-management-nav (config_role_id 16)
  | 'all-role'         // app-all-role-nav
  | null;

@Component({
    selector: 'app-main-page',
    templateUrl: './main-page.component.html',
    styleUrls: ['./main-page.component.css'],
    standalone: false
})
export class MainPageComponent implements OnInit, OnDestroy {
  private static readonly SYSTEM_ADMIN_EXCEPTION_PERMISSIONS = [
    AUTH_PERMISSIONS['system-admin-corporation'],
    AUTH_PERMISSIONS['system-admin-freesites'],
  ] as const;

  userDetail: any;
  private storage = environment.storage;
  userRle: number;
  useLegacyHeader = false;
  visitedUserManagementSites = false;

  configRoleId: number | null = null;
  navbarVariant: NavbarVariant = null;
  navbarUserRole: number | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private userService: UserManagementService,
    private userRoleService: UserRoleService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.userDetail = this.userService.getUserDetail();
    const permissionRoleId = this.permissionService.getRoleInfo()?.roleId;
    const resolvedRole = Number(
      permissionRoleId ??
        this.userDetail?.role ??
        this.userDetail?.user_role ??
        this.userDetail?.role_id ??
        0
    );

    this.useLegacyHeader =
      String(this.userDetail?.role ?? this.userDetail?.user_role ?? '') === '1' ||
      this.permissionService.hasAllPermissions(
        [...MainPageComponent.SYSTEM_ADMIN_EXCEPTION_PERMISSIONS]
      );

    this.userRle = resolvedRole;

    if (resolvedRole && this.userRoleService.getUserRole() == null) {
      this.userRoleService.initializeUserRole(resolvedRole);
    }

    const roleSub = this.userRoleService.userRole$.subscribe((role) => {
      this.userRle = role;
    });
    this.subscriptions.push(roleSub);

    const configRoleSub = this.permissionService.configRoleId$.subscribe(
      (configRoleId) => {
        this.configRoleId = configRoleId;
        this.navbarVariant = this.resolveNavbarVariant(configRoleId);
        this.navbarUserRole = this.resolveNavbarUserRole(configRoleId, this.userRle);
      }
    );
    this.subscriptions.push(configRoleSub);

    this.visitedUserManagementSites = sessionStorage.getItem('visitedUserManagementSites') === 'true';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Map the backend-provided `config_role_id` to the navbar variant rendered in
   * the template. Configs 4, 5, 10 are unspecified and fall through to `null`,
   * which hides every navbar variant.
   */
  private resolveNavbarVariant(configRoleId: number | null): NavbarVariant {
    if (configRoleId === null) {
      return null;
    }

    switch (configRoleId) {
      case 1:
        return 'legacy-header';
      case 2:
      case 7:
      case 8:
      case 11:
      case 13:
      case 14:
      case 15:
        return 'shared-user';
      case 3:
      case 9:
        return 'operator';
      case 6:
        return 'corporate-admin';
      case 12:
        return 'executive';
      case 16:
        return 'user-management';
      default:
        return null;
    }
  }

  private resolveNavbarUserRole(
    configRoleId: number | null,
    fallbackRole: number | null,
  ): number | null {
    if (configRoleId === null) {
      return fallbackRole;
    }

    switch (configRoleId) {
      case 2:
      case 7:
      case 8:
      case 11:
      case 13:
      case 14:
      case 15:
        return configRoleId;
      default:
        return fallbackRole;
    }
  }

  get currentUrl() {
    return this.router.url;
  }

  get showAllRoleNav(): boolean {
    return this.userRle < 1 || this.userRle > 16;
  }
}
